import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Card,
  CardHeader,
  CardMedia,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Fab,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  listBuddies,
  listBuddyRequests,
  searchUsersForBuddy,
  sendBuddyInvite,
  respondBuddyRequest,
  cancelBuddyRequest,
  removeBuddy,
  fetchBuddiesFeed,
  fetchBuddiesInitData,
} from '../api';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function BuddiesPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debouncedQ = useDebounced(query);

  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [loadingReq, setLoadingReq] = useState(true);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [feed, setFeed] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addBuddyDialogOpen, setAddBuddyDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedCatch, setSelectedCatch] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [localLikes, setLocalLikes] = useState({});

  // Load buddies, requests, and feed in one batch call (3→1)
  const loadAllBuddyData = async () => {
    setLoadingFriends(true);
    setLoadingReq(true);
    setLoadingFeed(true);
    try {
      const data = await fetchBuddiesInitData(token);
      setFriends(Array.isArray(data.buddies) ? data.buddies : []);
      setRequests(data.requests || { incoming: [], outgoing: [] });
      setFeed(Array.isArray(data.feed) ? data.feed : []);
    } catch (err) {
      // Fallback to individual fetches
      try {
        const friendsData = await listBuddies(token);
        setFriends(Array.isArray(friendsData) ? friendsData : []);
      } catch {}
      try {
        const reqData = await listBuddyRequests(token);
        setRequests(reqData || { incoming: [], outgoing: [] });
      } catch {}
      try {
        const feedData = await fetchBuddiesFeed(token);
        setFeed(Array.isArray(feedData) ? feedData : []);
      } catch {}
    } finally {
      setLoadingFriends(false);
      setLoadingReq(false);
      setLoadingFeed(false);
    }
  };

  useEffect(() => {
    loadAllBuddyData();
  }, [token]);

  useEffect(() => {
    if (!addBuddyDialogOpen || !debouncedQ) {
      setSearchResults([]);
      return;
    }
    const run = async () => {
      setSearching(true);
      try {
        const data = await searchUsersForBuddy(token, debouncedQ);
        setSearchResults(Array.isArray(data) ? data : []);
      } finally {
        setSearching(false);
      }
    };
    run();
  }, [debouncedQ, token, addBuddyDialogOpen]);

  const renderAvatar = (user) => (
    <Avatar
      src={(() => {
        const pic = user?.profile_picture;
        if (!pic) return undefined;
        if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
        const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
        return `${API_URL}/${normalized}`;
      })()}
    />
  );

  const resolveImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const normalized = path.startsWith('/') ? path.slice(1) : path;
    return `${API_URL}/${normalized}`;
  };

  const handleLike = async (catchId) => {
    if (!token) {
      setError('You need to be logged in to like catches');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/catches/${catchId}/like`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Like failed');
      const data = await res.json();
      setFeed((prev) =>
        prev.map((item) =>
          item.id === catchId ? { ...item, like_count: data.like_count } : item
        )
      );
      setLocalLikes((prev) => ({ ...prev, [catchId]: !!data.liked }));
    } catch (e) {
      setError(e.message || 'Failed to like catch');
    }
  };

  const openComments = async (catchItem) => {
    setSelectedCatch(catchItem);
    setComments([]);
    setNewComment('');
    setCommentsDialogOpen(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/catches/${catchItem.id}/comments`, { headers });
      if (!res.ok) throw new Error('Failed to load comments');
      const data = await res.json();
      setComments(data);
    } catch (e) {
      setError(e.message || 'Failed to load comments');
    }
  };

  const closeComments = () => {
    setCommentsDialogOpen(false);
    setSelectedCatch(null);
  };

  const handleAddComment = async () => {
    if (!token || !selectedCatch || !newComment.trim()) {
      if (!token) setError('You need to be logged in to comment');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/catches/${selectedCatch.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: newComment.trim() }),
      });
      if (!res.ok) throw new Error('Failed to add comment');
      const created = await res.json();
      setComments((prev) => [...prev, created]);
      setNewComment('');
      setFeed((prev) =>
        prev.map((item) =>
          item.id === selectedCatch.id
            ? { ...item, comment_count: (item.comment_count || 0) + 1 }
            : item
        )
      );
    } catch (e) {
      setError(e.message || 'Failed to add comment');
    }
  };

  const handleCloseAddBuddyDialog = () => {
    setAddBuddyDialogOpen(false);
    setQuery('');
    setSearchResults([]);
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 10 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">TheTribe.</Typography>
        <Fab
          variant="extended"
          color="primary"
          size="small"
          onClick={() => {
            setAddBuddyDialogOpen(true);
            setQuery('');
            setSearchResults([]);
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Add Buddy
        </Fab>
      </Stack>
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      <Dialog
        open={addBuddyDialogOpen}
        onClose={handleCloseAddBuddyDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add buddy</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search by nickname or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Box sx={{ minHeight: 80 }}>
            {searching ? (
              <CircularProgress size={20} />
            ) : (
              <List>
                {searchResults.map((row) => (
                  <ListItem
                    key={row.user.id}
                    secondaryAction={
                      row.relationship === 'friend' ? (
                        <Chip label="Friend" color="success" size="small" />
                      ) : row.relationship === 'pending_outgoing' ? (
                        <Chip label="Invited" color="warning" size="small" />
                      ) : row.relationship === 'pending_incoming' ? (
                        <Chip label="Requested you" color="info" size="small" />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={async () => {
                            await sendBuddyInvite(token, row.user.id);
                            await loadAllBuddyData();
                            setQuery('');
                          }}
                        >
                          Invite
                        </Button>
                      )
                    }
                  >
                    <ListItemAvatar>{renderAvatar(row.user)}</ListItemAvatar>
                    <ListItemText primary={row.user.nickname} secondary={row.user.email} />
                  </ListItem>
                ))}
                {searchResults.length === 0 && !searching && debouncedQ && (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                    No users found.
                  </Typography>
                )}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddBuddyDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {requests.incoming.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Incoming requests</Typography>
          {loadingReq ? <CircularProgress size={20} /> : (
            <List>
              {requests.incoming.map((r) => (
                <ListItem key={r.request_id} secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" size="small" onClick={async () => {
                      await respondBuddyRequest(token, r.request_id, 'accept');
                      await loadAllBuddyData();
                    }}>Accept</Button>
                    <Button variant="outlined" size="small" onClick={async () => {
                      await respondBuddyRequest(token, r.request_id, 'decline');
                      await loadAllBuddyData();
                    }}>Decline</Button>
                  </Stack>
                }>
                  <ListItemAvatar>{renderAvatar(r.from_user)}</ListItemAvatar>
                  <ListItemText primary={r.from_user.nickname} secondary={r.from_user.email} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {loadingFeed ? (
        <CircularProgress size={20} />
      ) : feed.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No recent catches from buddies yet.</Typography>
      ) : (
        <Stack spacing={2}>
          {feed.map((item) => (
            <Card key={item.id} variant="outlined">
              <CardHeader
                avatar={
                  <Avatar
                    src={(() => {
                      const pic = item.author_profile_picture;
                      if (!pic) return undefined;
                      if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                      const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                      return `${API_URL}/${normalized}`;
                    })()}
                    alt={item.author_nickname}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (item.user_id) navigate(`/profile/${item.user_id}`);
                    }}
                  />
                }
                title={item.author_nickname}
                subheader={`${item.species}${item.size ? ` • ${item.size} cm` : ''} • ${new Date(item.caught_at).toLocaleString()}`}
              />
              {item.image_path && (
                <CardMedia
                  component="img"
                  image={resolveImageUrl(item.image_path)}
                  alt={item.species}
                />
              )}
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleLike(item.id)}
                    sx={{ mr: 0.5, color: localLikes[item.id] ? 'error.main' : 'inherit' }}
                  >
                    <FavoriteBorderIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => openComments(item)}
                  >
                    <ChatBubbleOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {(item.like_count || 0) === 1
                    ? '1 like'
                    : `${item.like_count || 0} likes`}
                </Typography>
                {item.comment_count > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openComments(item)}
                  >
                    {item.comment_count === 1
                      ? 'View 1 comment'
                      : `View all ${item.comment_count} comments`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={commentsDialogOpen} onClose={closeComments} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 0.5 }}>
          {selectedCatch
            ? `${selectedCatch.species}${selectedCatch.size ? ` (${selectedCatch.size} cm)` : ''}`
            : 'Comments'}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 360, overflowY: 'auto', pt: 0 }}>
          {comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Be the first to comment.
            </Typography>
          ) : (
            comments.map((c) => (
              <Box key={c.id} sx={{ display: 'flex', mb: 1.5 }}>
                <Box sx={{ mr: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      bgcolor: 'grey.700',
                    }}
                  >
                    {c.author_profile_picture && (
                      <img
                        src={resolveImageUrl(c.author_profile_picture)}
                        alt={c.author_nickname || 'User'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {c.author_nickname || 'Anonymous'}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {c.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.created_at}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2, pt: 0 }}>
          <MuiTextField
            placeholder="Add a comment..."
            fullWidth
            size="small"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, width: '100%' }}>
            <Button onClick={closeComments} sx={{ mr: 1 }}>
              Close
            </Button>
            <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
              Post
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 320, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Manage Buddies</Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Outgoing requests</Typography>
          {loadingReq ? <CircularProgress size={18} /> : (
            <List>
              {requests.outgoing.map((r) => (
                <ListItem key={r.request_id} secondaryAction={
                  <Button variant="outlined" size="small" onClick={async () => {
                    await cancelBuddyRequest(token, r.request_id);
                    await loadAllBuddyData();
                  }}>Cancel</Button>
                }>
                  <ListItemAvatar>{renderAvatar(r.to_user)}</ListItemAvatar>
                  <ListItemText primary={r.to_user.nickname} secondary={r.to_user.email} />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Friends</Typography>
          {loadingFriends ? <CircularProgress size={18} /> : (
            <List>
              {friends.map((u) => (
                <ListItem key={u.id} secondaryAction={
                  <Button variant="outlined" size="small" color="error" onClick={async () => {
                    await removeBuddy(token, u.id);
                    await reloadFriends();
                    // Refresh feed as friends changed
                    setLoadingFeed(true);
                    const data = await fetchBuddiesFeed(token, 50).catch(() => []);
                    setFeed(Array.isArray(data) ? data : []);
                    setLoadingFeed(false);
                  }}>Remove</Button>
                }>
                  <ListItemAvatar>{renderAvatar(u)}</ListItemAvatar>
                  <ListItemText primary={u.nickname} secondary={u.email} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </Container>
  );
}
